import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { jsPDF } from 'jspdf';
import { domToPng } from 'modern-screenshot';
import {
  Printer,
  X,
  Download,
  Copy,
  Check,
  QrCode,
  Barcode as BarcodeIcon,
  Layers,
  Settings2,
  Sparkles,
  ShieldCheck,
  BookOpen,
  Info,
  Maximize2,
  FileText,
  Sliders,
  RotateCcw,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import type { Book } from '../../../shared/types/index.js';
import { slugify } from '../../../shared/utils/stringUtils.js';

export type LabelPreset = 'standard' | 'retail' | 'compact' | 'minimal';
export type LabelTheme = 'modern' | 'monochrome' | 'classic';

interface BookLabelPrintModalProps {
  book: Book | null;
  isOpen: boolean;
  onClose: () => void;
}

export const BookLabelPrintModal: React.FC<BookLabelPrintModalProps> = ({
  book,
  isOpen,
  onClose,
  }) => {
  const [preset, setPreset] = useState<LabelPreset>('standard');
  const [theme, setTheme] = useState<LabelTheme>('modern');
  const [copies, setCopies] = useState<number>(4);
  const [paperLayout, setPaperLayout] = useState<'sheet' | 'single'>('sheet');

  useEffect(() => {
    console.log('[PrintFlow] Modal isOpen:', isOpen, 'Copies:', copies, 'Layout:', paperLayout);
  }, [isOpen, copies, paperLayout]);

  const [showQr, setShowQr] = useState<boolean>(true);
  const [showBarcode, setShowBarcode] = useState<boolean>(true);
  const [showMetadata, setShowMetadata] = useState<boolean>(true);
  const [showBorderGuide, setShowBorderGuide] = useState<boolean>(true);
  const [barcodeFormat, setBarcodeFormat] = useState<'compact' | 'serial' | 'full'>('full');
  const [urlPrintFormat, setUrlPrintFormat] = useState<'short' | 'slug' | 'hidden'>('short');
  const [customSubtitle, setCustomSubtitle] = useState<string>('');
  const [copiedUibn, setCopiedUibn] = useState<boolean>(false);
  const [isExportingPng, setIsExportingPng] = useState<boolean>(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

  const printAreaRef = useRef<HTMLDivElement>(null);
  const singleLabelCanvasRef = useRef<HTMLCanvasElement>(null);

  // Standalone downloads for graphic design & cover printing
  const downloadSingleBarcodeSvg = () => {
    try {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const targetCode =
        barcodeFormat === 'compact'
          ? book.uibn.replace(/^UIBN-/i, '')
          : barcodeFormat === 'serial'
          ? book.uibn.replace(/\D/g, '') || book.uibn
          : book.uibn;

      JsBarcode(svg, targetCode, {
        format: 'CODE128',
        lineColor: '#000000',
        width: 1.5,
        height: 44,
        displayValue: true,
        text: targetCode,
        font: 'JetBrains Mono',
        fontSize: 12,
        textMargin: 8,
        margin: 8,
        marginBottom: 14,
        background: '#ffffff',
      });

      const currentHeight = parseFloat(svg.getAttribute('height') || '78');
      const currentWidth = parseFloat(svg.getAttribute('width') || '240');
      svg.setAttribute('viewBox', `0 0 ${currentWidth} ${currentHeight}`);

      let svgData = new XMLSerializer().serializeToString(svg);
      if (!svgData.includes('xmlns="http://www.w3.org/2000/svg"')) {
        svgData = svgData.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ');
      }
      const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `barcode-kompak-${barcodeFormat}-${book.uibn}.svg`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export single barcode SVG:', e);
    }
  };

  const downloadSingleBarcodePng = () => {
    try {
      const canvas = document.createElement('canvas');
      const targetCode =
        barcodeFormat === 'compact'
          ? book.uibn.replace(/^UIBN-/i, '')
          : barcodeFormat === 'serial'
          ? book.uibn.replace(/\D/g, '') || book.uibn
          : book.uibn;

      // 600 DPI Ultra High Resolution supersampling (~4000px wide master PNG, perfect for professional book offset printing)
      const scale = 16; // Upgraded from 8 to 16
      const barcodeBarsCanvas = document.createElement('canvas');
      const displayLabel = targetCode;

      JsBarcode(barcodeBarsCanvas, targetCode, {
        format: 'CODE128',
        lineColor: '#000000',
        width: 3.2 * scale, // Scaled up to double for Ultra HD crisp lines
        height: 80 * scale,  // Taller barcode ratio for better laser reading
        displayValue: false,
        margin: 5 * scale,
        background: '#ffffff',
      });

      const textSpacing = Math.round(48 * (scale / 4));
      const masterCanvas = document.createElement('canvas');
      masterCanvas.width = barcodeBarsCanvas.width;
      masterCanvas.height = barcodeBarsCanvas.height + textSpacing;

      const ctx = masterCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, masterCanvas.width, masterCanvas.height);
        ctx.drawImage(barcodeBarsCanvas, 0, 0);

        const textY = barcodeBarsCanvas.height + Math.round(textSpacing * 0.44);
        const fontSize = Math.round(30 * (scale / 4));
        const marginX = (5 * scale) + Math.round(scale * 1.5);

        // 1. Code Number (Centered for serial format, Left for others)
        ctx.fillStyle = '#000000'; // Pure black text for high legibility
        ctx.font = `800 ${fontSize}px "JetBrains Mono", monospace`;
        ctx.textBaseline = 'middle';

        if (barcodeFormat === 'serial') {
          ctx.textAlign = 'center';
          ctx.fillText(displayLabel, masterCanvas.width / 2, textY);
        } else {
          ctx.textAlign = 'left';
          ctx.fillText(displayLabel, marginX, textY);

          // 2. Right: uibn.web.id (Only for non-serial formats)
          ctx.fillStyle = '#4f46e5';
          ctx.font = `800 ${Math.round(fontSize * 0.94)}px "Inter", sans-serif`;
          ctx.textAlign = 'right';
          ctx.fillText('uibn.web.id', masterCanvas.width - marginX, textY);
        }
      }

      const pngUrl = (ctx ? masterCanvas : barcodeBarsCanvas).toDataURL('image/png');
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = `barcode-ultra-hd-600dpi-${barcodeFormat}-${book.uibn}.png`;
      link.click();
    } catch (e) {
      console.error('Failed to export single barcode PNG:', e);
    }
  };

  const downloadSingleQrPng = () => {
    try {
      const canvas = document.createElement('canvas');
      QRCode.toCanvas(
        canvas,
        verificationUrl,
        {
          width: 1200, // Ultra HD 1200x1200px resolution
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' },
          errorCorrectionLevel: 'H',
        },
        (err) => {
          if (err) return;
          const link = document.createElement('a');
          link.href = canvas.toDataURL('image/png');
          link.download = `qrcode-hd-300dpi-${book.uibn}.png`;
          link.click();
        }
      );
    } catch (e) {
      console.error('Failed to export QR PNG:', e);
    }
  };

  const downloadSingleQrSvg = async () => {
    try {
      const svgStr = await QRCode.toString(verificationUrl, {
        type: 'svg',
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
        errorCorrectionLevel: 'H',
      });
      const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qrcode-kompak-${book.uibn}.svg`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to export QR SVG:', e);
    }
  };

  useEffect(() => {
    console.log('[PrintFlow] Modal mounted/unmounted. Current book:', book?.uibn);
    if (isOpen && book) {
      setTimeout(() => {
        document.getElementById('btn-close-label-modal')?.focus();
      }, 100);
    }
  }, [isOpen, book]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove('printing-mode');
      return;
    }
    
    document.body.classList.add('printing-mode');

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('printing-mode');
    };
  }, [isOpen, onClose]);

  if (!isOpen || !book) {
    if (isOpen && !book) console.warn('[PrintFlow] Modal requested but book is null');
    return null;
  }

  console.log('[PrintFlow] Rendering modal for book:', book.uibn);

  const handlePrint = async () => {
    if (!printAreaRef.current) return;
    
    console.log('[PrintFlow] handlePrint called. totalLabels:', totalLabels, 'Layout:', paperLayout);
    setIsGeneratingPdf(true);

    try {
      const isSingle = paperLayout === 'single';
      const targetWidthMm = isSingle ? 100 : 210;
      const targetHeightMm = isSingle ? 150 : 297;
      
      // Calculate pixel dimensions at 96 DPI (standard CSS pixel mapping)
      const targetWidthPx = Math.round(targetWidthMm * 3.779527559);
      const targetHeightPx = Math.round(targetHeightMm * 3.779527559);

      console.log(`[PrintFlow] Capturing printArea at width: ${targetWidthPx}px (${targetWidthMm}mm), height: ${targetHeightPx}px (${targetHeightMm}mm)`);

      // 1. Capture the element as a PNG using modern-screenshot with forced target size
      // This bypasses any viewport squeezing or scroll clipping on mobile screens
      const dataUrl = await domToPng(printAreaRef.current, {
        scale: 3, // Premium high-resolution scaling for ultra-crisp barcode/QR scanning
        backgroundColor: '#ffffff',
        width: targetWidthPx,
        height: targetHeightPx,
        style: {
          width: `${targetWidthMm}mm`,
          height: `${targetHeightMm}mm`,
          minWidth: `${targetWidthMm}mm`,
          minHeight: `${targetHeightMm}mm`,
          maxWidth: `${targetWidthMm}mm`,
          maxHeight: `${targetHeightMm}mm`,
          margin: '0',
          padding: isSingle ? '4mm' : '8mm', // Apply professional margins to the print sheet
          transform: 'none',
          boxShadow: 'none',
          borderRadius: '0',
          border: 'none',
          display: 'block',
        }
      });

      // 2. Create PDF with EXACT matching page format
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: isSingle ? [100, 150] : 'a4',
      });

      // Render the image EXACTLY covering the page to prevent any shifting or empty margins
      pdf.addImage(dataUrl, 'PNG', 0, 0, targetWidthMm, targetHeightMm);
      
      // 3. Output
      const fileName = `label-uibn-${book.uibn}-${Date.now()}.pdf`;
      pdf.save(fileName);
      
      console.log('[PrintFlow] PDF generated successfully via modern-screenshot');
    } catch (error) {
      console.error('[PrintFlow] Failed to generate PDF via modern-screenshot:', error);
      alert('Gagal membuat PDF. Silakan coba lagi. Pastikan koneksi internet stabil.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleCopyUibn = () => {
    navigator.clipboard.writeText(book.uibn);
    setCopiedUibn(true);
    setTimeout(() => setCopiedUibn(false), 2000);
  };

  // Checksum and metadata extraction
  const uibnParts = book.uibn.split('-');
  const checkDigit = uibnParts.length === 5 ? uibnParts[4] : '';
  const publisherCode = uibnParts.length === 5 ? uibnParts[2] : '';

  const bookSlug = book.uibn;
  const verificationUrl = `${window.location.origin}/buku/${encodeURIComponent(bookSlug)}`;

  const totalLabels = paperLayout === 'single' ? 1 : copies;

  const getGridColsClass = () => {
    if (paperLayout === 'single') return 'grid-cols-1';
    if (copies <= 8) return 'grid-cols-2';
    if (copies <= 12) return 'grid-cols-3';
    return 'grid-cols-4';
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && book && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/75 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto overscroll-contain print:p-0 print:bg-white print:static print:overflow-visible printable-modal-backdrop">
          {/* Container for Screen Modal */}
          <motion.div
            initial={{ scale: 0.95, y: 15, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 15, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-100 overflow-hidden print:max-w-none print:w-full print:max-h-none print:shadow-none print:border-0 print:rounded-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Hidden on Print */}
            <div className="px-4 py-3.5 sm:px-6 sm:py-4 border-b border-slate-200 bg-slate-50/95 flex items-center justify-between gap-3 shrink-0 print:hidden backdrop-blur-md">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-xs shrink-0">
                  <Printer className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-slate-900 truncate">
                      Cetak Label & Barcode UIBN
                    </h3>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-800 border border-indigo-200 shrink-0">
                      Printer-Friendly
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5 min-w-0">
                    <span className="font-medium text-slate-700 truncate max-w-[200px] xs:max-w-[280px] sm:max-w-md">{book.title}</span>
                    <span className="text-slate-300 shrink-0">•</span>
                    <span className="font-mono font-bold text-indigo-700 bg-indigo-50/90 px-1.5 py-0.5 rounded border border-indigo-200/80 shrink-0">
                      {book.uibn}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  id="btn-close-label-modal"
                  type="button"
                  onClick={onClose}
                  className="w-8 h-8 rounded-lg bg-slate-200/70 hover:bg-rose-100 text-slate-700 hover:text-rose-700 border border-slate-300 hover:border-rose-300 flex items-center justify-center transition cursor-pointer shrink-0"
                  title="Tutup (Esc)"
                >
                  <X className="w-4 h-4 stroke-[2.2]" />
                </button>
              </div>
            </div>

            {/* Modal Body: Two Column Controls & Live Print Preview */}
            <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-slate-200 print:block print:overflow-visible print:p-0">
              {/* Left Column: Configuration Controls (Hidden when Printing) */}
              <div className="lg:col-span-4 p-4 sm:p-5 bg-slate-50/50 space-y-4 overflow-y-auto text-xs print:hidden">
                {/* 1. Template Presets */}
                <div className="space-y-2">
                  <label className="font-bold text-slate-800 flex items-center gap-1.5">
                    <Sliders className="w-3.5 h-3.5 text-indigo-600" />
                    <span>Format / Model Label</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPreset('standard')}
                      className={`p-2.5 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                        preset === 'standard'
                          ? 'bg-indigo-50/80 border-indigo-400 text-indigo-900 shadow-2xs font-semibold'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="font-bold block">Standar Sampul</span>
                      <span className="text-[10px] text-slate-500 mt-0.5">Barcode + QR + Info</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreset('retail')}
                      className={`p-2.5 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                        preset === 'retail'
                          ? 'bg-indigo-50/80 border-indigo-400 text-indigo-900 shadow-2xs font-semibold'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="font-bold block">Retail & Sertifikasi</span>
                      <span className="text-[10px] text-slate-500 mt-0.5">Lencana Resmi + Grid</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreset('compact')}
                      className={`p-2.5 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                        preset === 'compact'
                          ? 'bg-indigo-50/80 border-indigo-400 text-indigo-900 shadow-2xs font-semibold'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="font-bold block">Punggung Buku</span>
                      <span className="text-[10px] text-slate-500 mt-0.5">Spine & Shelf Label</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreset('minimal')}
                      className={`p-2.5 rounded-xl border text-left transition cursor-pointer flex flex-col justify-between ${
                        preset === 'minimal'
                          ? 'bg-indigo-50/80 border-indigo-400 text-indigo-900 shadow-2xs font-semibold'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="font-bold block">Kode Minimalis</span>
                      <span className="text-[10px] text-slate-500 mt-0.5">Hanya Barcode & QR</span>
                    </button>
                  </div>
                </div>

                {/* 2. Paper & Layout Mode */}
                <div className="space-y-2 pt-2 border-t border-slate-200">
                  <label className="font-bold text-slate-800 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-indigo-600" />
                      <span>Tata Letak Halaman</span>
                    </span>
                  </label>
                  <div className="flex bg-slate-200/80 p-0.5 rounded-xl font-medium">
                    <button
                      type="button"
                      onClick={() => {
                        console.log('[PrintFlow] Changing layout to: sheet');
                        setPaperLayout('sheet');
                      }}
                      className={`flex-1 py-2 rounded-lg text-center transition cursor-pointer ${
                        paperLayout === 'sheet'
                          ? 'bg-white text-indigo-700 font-bold shadow-2xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Lembar Stiker A4 / Grid
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('[PrintFlow] Changing layout to: single');
                        setPaperLayout('single');
                      }}
                      className={`flex-1 py-2 rounded-lg text-center transition cursor-pointer ${
                        paperLayout === 'single'
                          ? 'bg-white text-indigo-700 font-bold shadow-2xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Stiker Tunggal (Thermal)
                    </button>
                  </div>

                  {paperLayout === 'sheet' && (
                    <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <span className="text-slate-600 font-semibold">Jumlah Salinan:</span>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {[2, 4, 8, 12, 16].map((num) => (
                          <button
                            key={num}
                            type="button"
                            onClick={() => {
                              console.log('[PrintFlow] Changing copies selector button clicked:', num);
                              setCopies(num);
                            }}
                            className={`w-9 h-9 sm:w-8 sm:h-8 rounded-xl font-mono font-bold text-xs flex items-center justify-center transition cursor-pointer border-2 ${
                              copies === num
                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-md scale-105'
                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* 3. Tampilan & Gaya Warna */}
                <div className="space-y-2 pt-2 border-t border-slate-200">
                  <label className="font-bold text-slate-800 flex items-center gap-1.5">
                    <Settings2 className="w-3.5 h-3.5 text-indigo-600" />
                    <span>Gaya & Tinta Cetak</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setTheme('modern')}
                      className={`p-2 rounded-lg border text-left flex items-center gap-2 cursor-pointer transition ${
                        theme === 'modern'
                          ? 'bg-white border-indigo-500 text-indigo-900 shadow-2xs font-semibold'
                          : 'bg-white/60 border-slate-200 text-slate-600 hover:bg-white'
                      }`}
                    >
                      <div className="w-3.5 h-3.5 rounded-full bg-indigo-600 border border-white shrink-0" />
                      <span>Warna Indigo Modern</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTheme('monochrome')}
                      className={`p-2 rounded-lg border text-left flex items-center gap-2 cursor-pointer transition ${
                        theme === 'monochrome'
                          ? 'bg-white border-slate-900 text-slate-950 shadow-2xs font-bold'
                          : 'bg-white/60 border-slate-200 text-slate-600 hover:bg-white'
                      }`}
                    >
                      <div className="w-3.5 h-3.5 rounded-full bg-slate-900 border border-white shrink-0" />
                      <span>Monokrom (Thermal B&W)</span>
                    </button>
                  </div>
                </div>

                {/* 4. Filter Elemen Label */}
                <div className="space-y-2 pt-2 border-t border-slate-200">
                  <span className="font-bold text-slate-800 block">Elemen yang Ditampilkan:</span>
                  <div className="space-y-1.5 bg-white p-2.5 rounded-xl border border-slate-200">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showBarcode}
                        onChange={(e) => setShowBarcode(e.target.checked)}
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                      />
                      <span className="text-slate-700 font-medium">Barcode 1D (Code128)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showQr}
                        onChange={(e) => setShowQr(e.target.checked)}
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                      />
                      <span className="text-slate-700 font-medium">QR Code 2D (Verifikasi Otentikasi)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showMetadata}
                        onChange={(e) => setShowMetadata(e.target.checked)}
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                      />
                      <span className="text-slate-700 font-medium">Metadata (Judul, Penulis, Penerbit)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showBorderGuide}
                        onChange={(e) => setShowBorderGuide(e.target.checked)}
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                      />
                      <span className="text-slate-700 font-medium">Garis Panduan Potong (Crop Marks)</span>
                    </label>
                  </div>
                </div>

                {/* 5. Optimasi Hemat Ruang Cetak (Barcode & URL) */}
                <div className="space-y-3 pt-2 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800 flex items-center gap-1">
                      <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                      <span>Optimasi Hemat Ruang Cetak</span>
                    </span>
                    <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200 font-bold">
                      Anti-Panjang
                    </span>
                  </div>

                  {/* Format Barcode 1D */}
                  <div className="space-y-1.5">
                    <span className="text-slate-600 text-[11px] font-semibold block">Format Barcode 1D:</span>
                    <div className="grid grid-cols-3 gap-1 bg-slate-200/70 p-1 rounded-lg text-[10px] font-medium">
                      <button
                        type="button"
                        onClick={() => setBarcodeFormat('compact')}
                        className={`py-1.5 px-1 rounded text-center transition cursor-pointer ${
                          barcodeFormat === 'compact'
                            ? 'bg-white text-indigo-700 font-bold shadow-2xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                        title="Tanpa prefix UIBN- (Menghemat 30% lebar barcode)"
                      >
                        Ringkas (-30%)
                      </button>
                      <button
                        type="button"
                        onClick={() => setBarcodeFormat('serial')}
                        className={`py-1.5 px-1 rounded text-center transition cursor-pointer ${
                          barcodeFormat === 'serial'
                            ? 'bg-white text-emerald-700 font-bold shadow-2xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                        title="Hanya Angka Seri (Menghemat 50% lebar barcode)"
                      >
                        Seri (-50%)
                      </button>
                      <button
                        type="button"
                        onClick={() => setBarcodeFormat('full')}
                        className={`py-1.5 px-1 rounded text-center transition cursor-pointer ${
                          barcodeFormat === 'full'
                            ? 'bg-white text-slate-900 font-bold shadow-2xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                        title="Format lengkap UIBN-..."
                      >
                        Lengkap
                      </button>
                    </div>
                  </div>

                  {/* Format URL Footer Cetak */}
                  <div className="space-y-1.5">
                    <span className="text-slate-600 text-[11px] font-semibold block">Teks URL di Bawah Label:</span>
                    <div className="grid grid-cols-3 gap-1 bg-slate-200/70 p-1 rounded-lg text-[10px] font-medium">
                      <button
                        type="button"
                        onClick={() => setUrlPrintFormat('short')}
                        className={`py-1.5 px-1 rounded text-center transition cursor-pointer ${
                          urlPrintFormat === 'short'
                            ? 'bg-white text-indigo-700 font-bold shadow-2xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                        title="URL pendek /buku/{id}"
                      >
                        URL Pendek
                      </button>
                      <button
                        type="button"
                        onClick={() => setUrlPrintFormat('hidden')}
                        className={`py-1.5 px-1 rounded text-center transition cursor-pointer ${
                          urlPrintFormat === 'hidden'
                            ? 'bg-white text-emerald-700 font-bold shadow-2xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                        title="Sembunyikan URL untuk menghemat ruang maksimal"
                      >
                        Sembunyi
                      </button>
                      <button
                        type="button"
                        onClick={() => setUrlPrintFormat('slug')}
                        className={`py-1.5 px-1 rounded text-center transition cursor-pointer ${
                          urlPrintFormat === 'slug'
                            ? 'bg-white text-slate-900 font-bold shadow-2xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                        title="URL judul lengkap"
                      >
                        URL Judul
                      </button>
                    </div>
                  </div>
                </div>

                {/* 6. Unduh Berkas Gambar Satuan (SVG & PNG Siap Desain) */}
                <div className="space-y-2 pt-2 border-t border-slate-200">
                  <span className="font-bold text-slate-800 flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5 text-indigo-600" />
                    <span>Unduh Gambar Satuan (SVG / PNG):</span>
                  </span>
                  <p className="text-[10px] text-slate-500 leading-tight">
                    Butuh berkas gambar ringkas untuk dimasukkan ke software tata letak sampul (InDesign, Illustrator, Photoshop, Canva)? Unduh langsung di bawah:
                  </p>
                  <div className="grid grid-cols-2 gap-1.5 pt-0.5">
                    <button
                      type="button"
                      onClick={downloadSingleBarcodeSvg}
                      className="px-2.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200 transition flex items-center justify-center gap-1 text-[10.5px] cursor-pointer"
                      title="Unduh Barcode Vektor SVG Ringkas (Bebas Pecah)"
                    >
                      <Download className="w-3 h-3" />
                      <span>Barcode (SVG)</span>
                    </button>
                    <button
                      type="button"
                      onClick={downloadSingleBarcodePng}
                      className="px-2.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition flex items-center justify-center gap-1 text-[10.5px] cursor-pointer shadow-2xs"
                      title="Unduh Barcode PNG Resolusi Tinggi 300 DPI"
                    >
                      <Download className="w-3 h-3" />
                      <span>Barcode (PNG)</span>
                    </button>
                    <button
                      type="button"
                      onClick={downloadSingleQrSvg}
                      className="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold border border-slate-300 transition flex items-center justify-center gap-1 text-[10.5px] cursor-pointer"
                      title="Unduh QR Code Vektor SVG Kotak Kompak"
                    >
                      <QrCode className="w-3 h-3 text-slate-600" />
                      <span>QR Code (SVG)</span>
                    </button>
                    <button
                      type="button"
                      onClick={downloadSingleQrPng}
                      className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-black text-white font-semibold transition flex items-center justify-center gap-1 text-[10.5px] cursor-pointer shadow-2xs"
                      title="Unduh QR Code PNG Resolusi Tinggi 300 DPI"
                    >
                      <QrCode className="w-3 h-3 text-white" />
                      <span>QR Code (PNG)</span>
                    </button>
                  </div>
                </div>

                {/* Quick Actions Note */}
                <div className="p-3 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-1.5">
                  <div className="flex items-center gap-1.5 text-indigo-900 font-bold text-[11px]">
                    <Info className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                    <span>Petunjuk Percetakan:</span>
                  </div>
                  <p className="text-[11px] text-indigo-800 leading-relaxed">
                    Pilih ukuran kertas A4 pada menu Print browser, centang opsi <em>"Background graphics"</em> untuk hasil grafis maksimal, dan setel margin ke <em>"None"</em> atau <em>"Default"</em>.
                  </p>
                </div>
              </div>

              {/* Right Column: Live Printable Sheet Viewport */}
              <div className="lg:col-span-8 p-4 sm:p-6 bg-slate-200/50 flex flex-col items-center justify-start overflow-auto max-h-[75vh] lg:max-h-[82vh] print:p-0 print:bg-white print:max-h-none print:overflow-visible w-full">
                {/* Sheet Container Header */}
                <div className="w-full max-w-2xl mb-3 flex items-center justify-between text-xs text-slate-500 print:hidden">
                  <span className="font-medium">
                    Pratinjau Cetak ({totalLabels} label • {paperLayout === 'single' ? 'Single Sticker' : 'Lembar Grid A4'})
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCopyUibn}
                      className="px-2 py-1 rounded bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 transition flex items-center gap-1 shadow-2xs font-mono text-[11px]"
                      title="Salin Nomor UIBN"
                    >
                      {copiedUibn ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                      <span>{copiedUibn ? 'Tersalin' : book.uibn}</span>
                    </button>
                  </div>
                </div>

                <div className="w-full overflow-x-auto flex justify-start sm:justify-center p-2">
                  {/* Actual Printable Page Area */}
                  <div
                    ref={printAreaRef}
                    id="uibn-printable-label-sheet"
                    className={`bg-white shadow-xl rounded-xl border border-slate-300 p-6 shrink-0 print:shadow-none print:border-0 print:p-0 print:m-0 print:w-full print:max-w-none print:rounded-none ${
                      paperLayout === 'single' ? 'w-[100mm] min-h-[150mm]' : 'w-[210mm] min-h-[297mm]'
                    } ${
                      theme === 'monochrome' ? 'print-theme-mono' : 'print-theme-modern'
                    }`}
                  >
                    {/* Sheet Header in Print (Optional subtle registration footer) */}
                    <div
                      className={`grid gap-4 print:gap-6 ${getGridColsClass()}`}
                    >
                      {Array.from({ length: totalLabels }).map((_, idx) => (
                        <SingleBookLabel
                          key={idx}
                          index={idx}
                          book={book}
                          preset={preset}
                          theme={theme}
                          showBarcode={showBarcode}
                          showQr={showQr}
                          showMetadata={showMetadata}
                          showBorderGuide={showBorderGuide}
                          verificationUrl={verificationUrl}
                          barcodeFormat={barcodeFormat}
                          urlPrintFormat={urlPrintFormat}
                        />
                      ))}
                    </div>

                    {/* Sheet Bottom Attribution (Print-only micro footer) */}
                    <div className="mt-4 pt-2 border-t border-slate-200 text-center text-[9px] text-slate-400 font-mono hidden print:block">
                      UIBN Official Registry • Universal Indonesian Book Number • {book.uibn} • Dicetak: {new Date().toLocaleDateString('id-ID')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions - Hidden on Print */}
            <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-3 shrink-0 print:hidden text-xs">
              <div className="flex items-center gap-2 text-slate-500">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>Label terverifikasi & kompatibel dengan pemindai barcode POS/1D & QR/2D.</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3.5 py-1.5 rounded-xl bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 font-semibold transition cursor-pointer"
                >
                  Tutup
                </button>
                <button
                  id="btn-footer-print-labels"
                  type="button"
                  disabled={isGeneratingPdf}
                  onClick={() => {
                    console.log(`[PrintFlow] Footer button "Cetak ${totalLabels} Label" clicked`);
                    handlePrint();
                  }}
                  className={`px-6 py-3.5 sm:px-5 sm:py-2.5 rounded-2xl sm:rounded-xl font-bold flex items-center justify-center gap-2 transition cursor-pointer shadow-lg active:scale-95 w-full sm:w-auto ${
                    isGeneratingPdf 
                      ? 'bg-slate-400 cursor-not-allowed' 
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  }`}
                >
                  {isGeneratingPdf ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Printer className="w-5 h-5 sm:w-4 sm:h-4" />
                  )}
                  <span className="text-sm sm:text-xs">
                    {isGeneratingPdf ? 'Memproses PDF...' : `Cetak ${totalLabels} Label (PDF)`}
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

/* Component: Single Printable Label Tile with dynamically generated barcode & QR */
interface SingleBookLabelProps {
  index: number;
  book: Book;
  preset: LabelPreset;
  theme: LabelTheme;
  showBarcode: boolean;
  showQr: boolean;
  showMetadata: boolean;
  showBorderGuide: boolean;
  verificationUrl: string;
  barcodeFormat?: 'compact' | 'serial' | 'full';
  urlPrintFormat?: 'short' | 'slug' | 'hidden';
}

const SingleBookLabel: React.FC<SingleBookLabelProps> = ({
  index,
  book,
  preset,
  theme,
  showBarcode,
  showQr,
  showMetadata,
  showBorderGuide,
  verificationUrl,
  barcodeFormat = 'compact',
  urlPrintFormat = 'short',
}) => {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  const compactUibn = book.uibn.replace(/^UIBN-/i, '');
  const serialDigits = book.uibn.replace(/\D/g, '') || '00000001';

  let targetCode = book.uibn;
  let labelDisplayText = book.uibn;
  if (barcodeFormat === 'compact') {
    targetCode = compactUibn;
    labelDisplayText = compactUibn;
  } else if (barcodeFormat === 'serial') {
    targetCode = serialDigits;
    labelDisplayText = serialDigits;
  } else if (barcodeFormat === 'full') {
    targetCode = book.uibn;
    labelDisplayText = book.uibn;
  }

  useEffect(() => {
    console.log(`[PrintFlow] Label ${index + 1} rendering. TargetCode:`, targetCode);
    // Generate Code128 Barcode with space-saving compact proportions
    if (barcodeRef.current && showBarcode && targetCode) {
      try {
        const svg = barcodeRef.current;
        while (svg.firstChild) {
          svg.removeChild(svg.firstChild);
        }

        const barWidth = preset === 'compact' ? 1.05 : (barcodeFormat === 'serial' ? 1.3 : 1.15);
        const barHeight = preset === 'compact' ? 28 : 34;
        const margin = 2;
        const fontSize = 10.5;

        JsBarcode(svg, targetCode, {
          format: 'CODE128',
          lineColor: '#000000',
          width: barWidth,
          height: barHeight,
          displayValue: false,
          margin: margin,
          background: '#ffffff',
        });

        const currentHeight = parseFloat(svg.getAttribute('height') || `${barHeight + margin * 2}`);
        const currentWidth = parseFloat(svg.getAttribute('width') || '160');

        // Add generous spacing for font height + bottom margin to prevent descenders clipping in SVG preview
        const textPadding = 8;
        const textSpacing = Math.round(fontSize + textPadding + 4);
        const newTotalHeight = Math.round(currentHeight + textSpacing);

        // CRITICAL FIX: Must set BOTH height AND viewBox so SVG does not cut off text at the bottom!
        svg.setAttribute('height', `${newTotalHeight}`);
        svg.setAttribute('viewBox', `0 0 ${currentWidth} ${newTotalHeight}`);

        const textY = Math.round(currentHeight + fontSize - 1);

        // 1. Left/Center Code (Larger & Bold Monospace)
        const leftText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        if (barcodeFormat === 'serial') {
          leftText.setAttribute('x', `${currentWidth / 2}`);
          leftText.setAttribute('text-anchor', 'middle');
        } else {
          leftText.setAttribute('x', `${margin + 1}`);
          leftText.setAttribute('text-anchor', 'start');
        }
        leftText.setAttribute('y', `${textY}`);
        leftText.setAttribute('fill', '#000000');
        leftText.setAttribute('font-family', '"JetBrains Mono", monospace');
        leftText.setAttribute('font-weight', '800');
        leftText.setAttribute('font-size', `${fontSize}px`);
        leftText.textContent = labelDisplayText;
        svg.appendChild(leftText);

        // 2. Right: uibn.web.id (Omitted for serial barcode format)
        if (barcodeFormat !== 'serial') {
          const rightText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          rightText.setAttribute('x', `${currentWidth - margin - 1}`);
          rightText.setAttribute('y', `${textY}`);
          rightText.setAttribute('text-anchor', 'end');
          rightText.setAttribute('fill', '#4f46e5');
          rightText.setAttribute('font-family', '"Inter", -apple-system, sans-serif');
          rightText.setAttribute('font-weight', '800');
          rightText.setAttribute('font-size', `${fontSize * 0.94}px`);
          rightText.textContent = 'uibn.web.id';
          svg.appendChild(rightText);
        }

      } catch (err) {
        console.error('Barcode render error in label:', err);
      }
    }

    // Generate QR Code
    if (qrCanvasRef.current && showQr && book.uibn) {
      QRCode.toCanvas(
        qrCanvasRef.current,
        verificationUrl,
        {
          width: preset === 'compact' ? 68 : 82,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#ffffff',
          },
          errorCorrectionLevel: 'M',
        },
        (err) => {
          if (err) console.error('QR code render error in label:', err);
        }
      );
    }
  }, [book.uibn, showBarcode, showQr, preset, verificationUrl, targetCode, labelDisplayText, barcodeFormat]);

  const isMono = theme === 'monochrome';

  // Render Label Variant according to Preset
  return (
    <div
      className={`relative bg-white p-3 rounded-lg overflow-hidden flex flex-col justify-between transition break-inside-avoid print:break-inside-avoid ${
        showBorderGuide
          ? 'border border-dashed border-slate-300 print:border-slate-400'
          : 'border border-transparent'
      }`}
      style={{ minHeight: preset === 'compact' ? '140px' : '170px' }}
    >
      {/* 1. Header Band */}
      <div className="flex items-center justify-between gap-1 pb-1.5 border-b border-slate-200">
        <div className="flex items-center gap-1.5">
          <div
            className={`px-1.5 py-0.5 rounded text-[9px] font-black tracking-wider uppercase ${
              isMono ? 'bg-black text-white' : 'bg-indigo-600 text-white'
            }`}
          >
            UIBN
          </div>
          <span className="text-[9px] font-bold text-slate-800 uppercase tracking-tight">
            Universal Indonesian Book Number
          </span>
        </div>
        <span className="text-[8px] font-mono font-bold text-slate-500">
          REG. INDONESIA
        </span>
      </div>

      {/* 2. Body based on Preset */}
      {preset === 'retail' && (
        <div className="py-2 space-y-2">
          {showMetadata && (
            <div>
              <h4 className="font-extrabold text-slate-950 text-xs leading-tight line-clamp-2">
                {book.title}
              </h4>
              <div className="flex items-center justify-between text-[10px] text-slate-600 mt-0.5">
                <span className="truncate max-w-[140px]">Oleh: {book.authorName}</span>
                <span className="font-semibold">{book.publicationYear || new Date().getFullYear()}</span>
              </div>
              <div className="text-[9px] text-slate-500 truncate">
                Penerbit: {book.publisherName} • {book.category}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            {showBarcode && (
              <div className="flex-1 flex flex-col items-center justify-center overflow-hidden">
                <svg ref={barcodeRef} className="max-w-full h-auto" />
              </div>
            )}
            {showQr && (
              <div className="shrink-0 flex flex-col items-center">
                <canvas ref={qrCanvasRef} className="rounded" />
                <span className="text-[8px] font-bold text-slate-500 mt-0.5">Pindai Verifikasi</span>
              </div>
            )}
          </div>
        </div>
      )}

      {preset === 'standard' && (
        <div className="py-2 space-y-1.5">
          {showMetadata && (
            <div>
              <h4 className="font-bold text-slate-900 text-xs leading-snug line-clamp-1">
                {book.title}
              </h4>
              <div className="text-[10px] text-slate-600 flex items-center justify-between">
                <span className="truncate max-w-[130px]">{book.authorName}</span>
                <span className="text-[9px] text-slate-500">{book.publisherName}</span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            {showBarcode && (
              <div className="flex-1 flex flex-col items-center justify-center overflow-hidden">
                <svg ref={barcodeRef} className="max-w-full h-auto" />
              </div>
            )}
            {showQr && (
              <div className="shrink-0 flex flex-col items-center">
                <canvas ref={qrCanvasRef} className="rounded" />
                <span className="text-[7.5px] font-bold text-slate-500 mt-0.5">Scan UIBN</span>
              </div>
            )}
          </div>
        </div>
      )}

      {preset === 'compact' && (
        <div className="py-1.5 flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            {showMetadata && (
              <div className="mb-1">
                <h4 className="font-bold text-slate-900 text-[11px] leading-tight truncate">
                  {book.title}
                </h4>
                <p className="text-[9px] text-slate-600 truncate">{book.authorName}</p>
              </div>
            )}
            {showBarcode && (
              <div className="flex flex-col items-start overflow-hidden">
                <svg ref={barcodeRef} className="max-w-full h-auto" />
              </div>
            )}
          </div>

          {showQr && (
            <div className="shrink-0 flex flex-col items-center">
              <canvas ref={qrCanvasRef} className="rounded" />
              <span className="text-[7px] text-slate-500 font-mono">VERIFIKASI</span>
            </div>
          )}
        </div>
      )}

      {preset === 'minimal' && (
        <div className="py-2 flex items-center justify-between gap-3">
          {showBarcode && (
            <div className="flex-1 flex flex-col items-center justify-center overflow-hidden">
              <svg ref={barcodeRef} className="max-w-full h-auto" />
            </div>
          )}
          {showQr && (
            <div className="shrink-0 flex flex-col items-center">
              <canvas ref={qrCanvasRef} className="rounded" />
              <span className="text-[7.5px] font-mono text-slate-500">{book.uibn.slice(-6)}</span>
            </div>
          )}
        </div>
      )}

      {/* 3. Footer Details */}
      <div className="pt-1 border-t border-slate-200 flex items-center justify-between text-[8px] font-mono text-slate-500">
        <span className="font-bold text-slate-800">{book.uibn}</span>
        {urlPrintFormat !== 'hidden' && (
          <span
            className="text-[7.5px] text-slate-500 truncate max-w-[170px]"
            title={
              urlPrintFormat === 'short'
                ? `uibn.web.id/buku/${book.id || compactUibn}`
                : `uibn.web.id/buku/${book.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`
            }
          >
            {urlPrintFormat === 'short'
              ? `uibn.web.id/buku/${book.id || compactUibn}`
              : `uibn.web.id/buku/${book.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`}
          </span>
        )}
      </div>
    </div>
  );
};
